/**
 * PrerenderReady — mounts once and calls `window.__PRERENDER_SIGNAL__()`
 * after the first paint, telling the post-build prerenderer that the DOM
 * is ready to be captured.
 *
 * The signal API is set up by scripts/prerender.mjs via
 * `page.evaluateOnNewDocument` before any of the app's JS runs. When this
 * component mounts:
 *
 *   - In the Puppeteer headless browser, `window.__PRERENDER_SIGNAL__`
 *     exists and we call it → prerenderer serializes the DOM and writes
 *     it to disk.
 *   - In real browsers, `window.__PRERENDER_SIGNAL__` is undefined, and
 *     the `typeof window !== 'undefined'` check ensures the effect no-ops
 *     cleanly. No runtime cost beyond one optional function call on mount.
 *
 * We use `requestAnimationFrame` to defer the signal until AFTER the browser
 * has painted. Without this, Puppeteer can capture the DOM before Helmet
 * has injected its head tags (Helmet does so in a post-commit effect too).
 * One rAF is enough in practice; a double rAF would be more conservative
 * but adds latency to every prerender.
 *
 * Rendering:
 *   - Once, at the top of <App>, inside <HelmetProvider> so head tags are
 *     captured too.
 *   - Renders null — this component is a side-effect, not UI.
 */

import { useEffect } from 'react';

export function PrerenderReady(): null {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w = window as any;
    const signal = w.__PRERENDER_SIGNAL__ as (() => void) | undefined;
    if (typeof signal !== 'function') return;

    // Defer until after paint so Helmet has flushed its <head> mutations.
    // Using rAF instead of setTimeout(0) because rAF is tied to the browser's
    // render pipeline, which is exactly what we want to synchronize against.
    const raf = requestAnimationFrame(() => {
      signal();
    });

    return () => cancelAnimationFrame(raf);
  }, []);

  return null;
}

export default PrerenderReady;
